import { CustomOption } from "@/common/custom-selectors/CustomOption";
import { format, isAfter, parse } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import { useForm } from "react-hook-form";
import { NotificationManager } from "react-notifications";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import Select from "react-select";
import { Actions as GROUPACTIONS } from "../../../store/actions/groupActions";
import { DateInputFormat } from "../../common/DateComponents";
import Spinner from "../../common/Spinner";
import { validateFloat, validateInteger } from "../../common/validation/Validators";

const AddGroupModal = ({ saveGroup, modalGroup, modalSaving, schemes, currency, permitted, modalGroupScheme }) => {
	const { handleSubmit } = useForm();
	const [schemeOptions, setSchemeOptions] = useState(schemes);
	const [groupName, setGroupName] = useState("");
	const [startDate, setStartDate] = useState(new Date());
	const [endDate, setEndDate] = useState(null);
	const [employeeContribution, setEmployeeContribution] = useState(0);
	const [employerContribution, setEmployerContribution] = useState(0);
	const [scheme, setScheme] = useState(schemes?.[0]);
	const [employeePercentage, setEmployeePercentage] = useState(true);
	const [employerPercentage, setEmployerPercentage] = useState(true);
	const [salarySacrifice, setSalarySacrifice] = useState(false);
	const [addTaxSavingsToSacrifice, setAddTaxSavingsToSacrifice] = useState(false);
	const [employerNICSavings, setEmployerNICSavings] = useState(0);

	const disabled = useMemo(() => {
		return !permitted || scheme?.isDisabled;
	}, [scheme?.value, permitted]);

	const percentageOptions = useMemo(() => {
		return [
			{ label: "%", value: true },
			{ label: currency, value: false },
		];
	}, [currency]);

	const onSubmit = (data) => {
		data.id = modalGroup?.id;
		data.scheme = scheme.value;
		data.name = groupName;
		data.startDate = format(startDate, "yyyy-MM-dd");
		data.endDate = endDate ? format(endDate, "yyyy-MM-dd") : "null";
		data.employeeContribution = parseFloat(employeeContribution);
		data.employerContribution = parseFloat(employerContribution);
		data.employeePercentage = employeePercentage;
		data.employerPercentage = employerPercentage;
		data.salarySacrifice = salarySacrifice;
		data.addTaxSavingsToSacrifice = addTaxSavingsToSacrifice;
		data.employerNICSavings = employerNICSavings;

		const hasEnd = scheme?.endDate && scheme?.endDate !== "null";
		if (hasEnd) {
			const schemeEnd = parse(scheme?.endDate, "yyyy-MM-dd", new Date());
			if (isAfter(schemeEnd, endDate)) {
				NotificationManager.error("Group End Date Cannot Be After Scheme End Date", "Error Saving Group");
				return;
			}
		}

		const hasNegatives = data.employerContribution < 0 || data.employeeContribution < 0;

		if (hasNegatives) {
			NotificationManager.error("Contribution Amounts Cannot Be Negative", "Error Saving Group");
			return;
		}

		// proper field validation on contribution amounts is done on auto enrolment
		const hasMissingFields = !data.name.replace(/ /g, "")?.length || !data.scheme;

		if (hasMissingFields) {
			NotificationManager.error("Please Ensure All Fields Are Filled!", "Error Saving Group");
			return;
		}

		if (employeePercentage && employerPercentage) {
			const totalContribution = parseFloat(employeeContribution) + parseFloat(employerContribution);
			if (!modalGroupScheme?.autoEnrolmentExcluded){
				if (employerContribution < 3) {
					NotificationManager.error(
						`Employer Contributions cannot be less than 3%`,
						"Saving Pension Plan",
					);
					return;
				}
				if (totalContribution < 8) {
					NotificationManager.error(
						`Total Contributions cannot be less than 8%`,
						"Saving Pension Plan",
					);
					return;
				}
			}
		}
		saveGroup(data);
	};

	useEffect(() => {
		if (modalGroup) {
			if (modalGroup.startDate) setStartDate(parse(modalGroup.startDate, "yyyy-MM-dd", new Date()));
			if (modalGroup.endDate && modalGroup.endDate !== "null")
				setEndDate(parse(modalGroup.endDate, "yyyy-MM-dd", new Date()));
			setGroupName(modalGroup.name);
			const { employeePercentage, employerPercentage, employeeContribution, employerContribution } = modalGroup;

			setEmployeeContribution(parseFloat(employeeContribution));
			setEmployerContribution(parseFloat(employerContribution));
			setEmployeePercentage(!!employeePercentage);
			setEmployerPercentage(!!employerPercentage);
			setScheme(
				modalGroup?.scheme
					? schemes?.find((scheme) => scheme.value == modalGroup.scheme) || {
							value: modalGroup.scheme,
							label: "Deleted Scheme",
					  }
					: null,
			);
			setSalarySacrifice(modalGroup.salarySacrifice);
			setAddTaxSavingsToSacrifice(modalGroup.addTaxSavingsToSacrifice);
			setEmployerNICSavings(modalGroup.employerNICSavings);
		} else {
			setGroupName();
			setStartDate();
			setEndDate();
			setEmployeeContribution(0);
			setEmployerContribution(0);
			setEmployeePercentage(true);
			setEmployerPercentage(true);
			setScheme(null);
		}
	}, [modalGroup]);

	return (
		<form
			onSubmit={handleSubmit(onSubmit)}
			style={{ paddingRight: 50, overflow: "visible" }}
		>
			<div
				className="form-group"
				style={{ marginTop: 50 }}
			>
				<div className="col-xs-12">
					<div className="col-xs-6 pl-0">
						<div className="form-material">
							<label htmlFor="material-text">Group Start Date</label>
							<DatePicker
								showMonthDropdown
								showYearDropdown
								autoComplete="off"
								dateFormat="dd/MM/yyyy"
								disabled={disabled}
								className="form-control"
								required
								wrapperClassName="full-width"
								selected={startDate}
								onSelect={(date) => {
									setStartDate(date);
								}}
								customInput={<DateInputFormat />}
							/>
						</div>
						<div className="clearfix" />
					</div>
					<div className="col-xs-6 pl-0">
						<div className="form-material">
							<label htmlFor="material-text">Group End Date</label>
							<DatePicker
								showMonthDropdown
								showYearDropdown
								autoComplete="off"
								disabled={disabled}
								dateFormat="dd/MM/yyyy"
								className="form-control"
								wrapperClassName="full-width"
								selected={endDate}
								maxDate={
									scheme?.endDate && scheme?.endDate !== "null"
										? parse(scheme?.endDate, "yyyy-MM-dd", new Date())
										: null
								}
								onChange={(date) => {
									setEndDate(date);
								}}
								customInput={<DateInputFormat />}
							/>
						</div>
						<div className="clearfix" />
					</div>
					<div
						className="col-xs-6"
						style={{ paddingLeft: 0 }}
					>
						<div className="form-material">
							<label htmlFor="material-text">Group Name</label>
							<input
								className="form-control"
								required
								disabled={disabled}
								value={groupName}
								onChange={(e) => {
									setGroupName(e.target.value);
								}}
								autoComplete="off"
							/>
						</div>
						<div className="clearfix" />
					</div>
					<div
						className="col-xs-6"
						style={{ paddingLeft: 0 }}
					>
						<div className="form-material">
							<label htmlFor="material-text">Scheme Used</label>
							<Select
								as={Select}
								options={schemes}
								components={{ Option: CustomOption }}
								isDisabled={disabled}
								value={scheme}
								onChange={(selected) => {
									setScheme(selected);
								}}
							/>
						</div>
						<div className="clearfix" />
					</div>
				</div>
				<div className="col-xs-12">
					<div
						className="col-xs-6"
						style={{ paddingLeft: 0 }}
					>
						<div className="col-xs-8 pl-0 pr-0">
							<div className="form-material">
								<label htmlFor="material-text">Employee Contribution</label>
								<input
									className="form-control form-input"
									required
									disabled={disabled}
									value={employeeContribution}
									onChange={(e) => {
										setEmployeeContribution(validateFloat(e.target.value));
									}}
									autoComplete="off"
								/>
							</div>
						</div>
						<div className="col-xs-4 pr-0">
							<div className="form-material">
								<Select
									value={percentageOptions?.find(({ value }) => value === employeePercentage)}
									isDisabled={disabled}
									onChange={(selected) => {
										setEmployeePercentage(selected?.value);
									}}
									options={percentageOptions}
								/>
							</div>
						</div>
					</div>
					<div
						className="col-xs-6"
						style={{ paddingLeft: 0 }}
					>
						<div className="col-xs-8 pl-0 pr-0">
							<div className="form-material">
								<label htmlFor="material-text">Employer Contribution</label>
								<input
									className="form-control form-input"
									required
									disabled={disabled}
									value={employerContribution}
									onChange={(e) => {
										setEmployerContribution(validateFloat(e.target.value));
									}}
									autoComplete="off"
								/>
							</div>
						</div>
						<div className="col-xs-4 pr-0">
							<div className="form-material">
								<Select
									value={percentageOptions?.find(({ value }) => value === employerPercentage)}
									isDisabled={disabled}
									onChange={(selected) => {
										setEmployerPercentage(selected?.value);
									}}
									options={percentageOptions}
								/>
							</div>
						</div>
					</div>
				</div>
				<div className="col-xs-12">
					<div className="col-xs-6"
						style={{ paddingLeft: 0, marginBottom: 10 }}
					>
						<div>
							<label className="css-input switch switch-sm switch-success">
								<input
									type="checkbox"
									checked={salarySacrifice}
									onChange={(e) => {
										setSalarySacrifice(e.target.checked);
										if (e.target.checked == true) return;

										setAddTaxSavingsToSacrifice(false);
										setEmployerNICSavings(0);
									}}
									disabled={disabled}
								/>
								<span /> Salary Sacrifice
							</label>
						</div>
						<div className="help-block">
							For pensions reporting purposes, all sacrificed contributions will be transferred
							to Employer Contributions once salary sacrifice is computed.
						</div>
					</div>
				</div>
				<div
					className="col-xs-12"
					style={{
						pointerEvents: salarySacrifice ? "auto" : "none",
						opacity: salarySacrifice ? 1 : 0,
						transition: "opacity 0.5s ease-in-out",
					}}
				>
					<div className="col-xs-6"
							style={{ paddingLeft: 0 }}
					>
						<div>
							<label className="css-input switch switch-sm switch-success">
								<input
									type="checkbox"
									checked={addTaxSavingsToSacrifice}
									onChange={(e) => {
										setAddTaxSavingsToSacrifice(e.target.checked);
									}}
									disabled={disabled}
								/>
								<span /> Sacrifice Tax Savings
							</label>
						</div>
						<div className="help-block">
							Adds an additional 20% to the salary sacrifice in reflection to Income Tax Relief saved.
						</div>
					</div>
					<div className="col-xs-6"
						style={{ paddingLeft: 0}}
					>
						<div className="form-material">
							<label htmlFor="material-text">Employer NIC Savings (% Savings passed to Employee)</label>
							<input
								className="form-control"
								value={employerNICSavings}
								disabled={disabled}
								onChange={(e) => {
									const value = validateInteger(e.target.value);
									if (value > 100 || value < 0) return;

									setEmployerNICSavings(value);
								}}
								autoComplete="off"
							/>
						</div>
					</div>
				</div>

				<div style={{ paddingRight: 0 }}>
					<button
						className="btn btn-success pull-right"
						disabled={disabled}
						style={{ marginTop: 20 }}
					>
						{!modalSaving ? (
							modalGroup ? (
								"Edit"
							) : (
								"Create"
							)
						) : (
							<>
								<Spinner
									style={{ height: "1.5em", verticalAlign: "middle", float: "left" }}
									className="animate-spin -ml-1 mr-3 h-5 w-5 "
								/>
								<div style={{ float: "left" }}> Saving </div>
							</>
						)}
					</button>
					<div className="clearfix" />
				</div>
			</div>
		</form>
	);
};

const mapStateToProps = (state) => {
	const pensionIntegrations = Object.values({ ...state?.integration?.pension }).map(({ name, canSyncUp }) => ({
		value: name,
		label: name,
		isDisabled: !canSyncUp,
		statusMessage: !canSyncUp ? (
			<div>
				{name} does not support creation of groups outside of their product. To import a scheme, please use
				the&nbsp;
				<Link to="/organisation/settings/integrations">import feature</Link>
			</div>
		) : undefined,
	}));

	const modalGroup = state?.pensions?.group?.modalGroup;

	const schemeOptions = [...state?.pensions?.scheme?.list]?.map((scheme) => {
		const providerOptions = pensionIntegrations?.find(({ value }) => value === scheme.provider);

		return {
			value: scheme.id,
			label: scheme.name,
			isDisabled: providerOptions?.isDisabled,
			statusMessage: providerOptions?.statusMessage,
			startDate: scheme.startDate,
			endDate: scheme.endDate,
			autoEnrolmentExcluded: scheme.autoEnrolmentExcluded,
		};
	});

	return {
		modalGroup,
		modalSaving: state?.pensions?.group?.modalSaving,
		schemes: schemeOptions,
		pensionIntegrations,
		currency: state?.company?.settings?.currency,
		permitted: true,
		modalGroupScheme: schemeOptions.find((scheme) => scheme?.value === modalGroup?.scheme),
	};
};

const mapDispatchToProps = (dispatch) => ({
	saveGroup: (group) => dispatch(GROUPACTIONS.saveGroup(group)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AddGroupModal);
